import {
  Link as MuiLink,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@material-ui/core';
import {
  ErrorOutline as ErrorOutlineIcon,
  ExpandMore as ExpandMoreIcon
} from '@material-ui/icons';
import Typography from '@material-ui/core/Typography';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    background: '#f6f7f9',
    color: theme.palette.primary.contrastText,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column-reverse'
    }
  },
  columnLeft: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
    flex: 2,
    overflowX: 'auto',
    [theme.breakpoints.down('md')]: {
      backgroundColor: 'initial'
    }
  },
  columnLeftBloc: {
    width: '100%',
    height: '85vh',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  blocUp: {
    height: '80%',
    width: '70%',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column'
  },
  blocDown: {
    height: '20%',
    width: '70%',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column'
  },
  columnRight: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#d3b16f',
    height: '100%',
    boxShadow: 'inset 2px 0px 4px #6969699e',
    flex: 1,
    [theme.breakpoints.down('md')]: {
      boxShadow: 'none',
      padding: '20px 0',
      borderRadius: 16
    }
  },
  blocRight: {
    height: '70%',
    width: '70%',
    display: 'flex',
    alignItems: 'flex-end',
    margin: '0 auto'
  },
  title: {
    fontSize: 45,
    textTransform: 'uppercase',
    color: '#d3b16f',
    fontWeight: 'bold',
    marginBottom: 10
  },
  subtitle: {
    fontSize: 43,
    marginBottom: 15,
    fontWeight: '600',
    color: '#000'
  },
  paragraph: {
    fontSize: 16,
    marginBottom: 18,
    cursor: 'pointer'
  },
  blocError: {
    width: '70%',
    height: 0,
    marginTop: 40
  },
  listItem: {
    padding: 0
  }
}));

const BoundaryErrorPage = ({ error, info }) => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [showError, setShowError] = useState(false);

  return (
    <>
      <div
        className={clsx(
          classes.root,
          'flex-auto p-16 sm:p-24 md:flex-row md:p-0 overflow-hidden'
        )}
      >
        <div className={classes.columnLeft}>
          <div className={classes.columnLeftBloc}>
            <div className={classes.blocUp}>
              <h1 className={classes.title}>OUPS !</h1>
              <h2 className={classes.subtitle}>{t('errors.boundary.title')}</h2>
            </div>
            <div className={classes.blocDown}>
              <p
                style={{ color: '#d3b16f' }}
                className={classes.paragraph}
                onClick={() => setShowError(true)}
              >
                {t('show_details')}
              </p>
              <MuiLink href={pathname}>
                <p style={{ color: '#000' }} className={classes.paragraph}>
                  {t('errors.boundary.try_again')}
                </p>
              </MuiLink>
            </div>
          </div>
          <div className={classes.blocError}>
            {info?.componentStack && showError && (
              <>
                <Typography
                  variant="h4"
                  color="error"
                  style={{ fontSize: '2.5rem' }}
                >
                  {error.toString()}
                </Typography>
                <List className={classes.list}>
                  {info.componentStack.split('   ').map((line, key) => (
                    <ListItem
                      key={`boundary-error-${key}`}
                      className={classes.listItem}
                    >
                      <ListItemText
                        primaryTypographyProps={{
                          color: 'error',
                          fontSize: '1.6rem'
                        }}
                        primary={line}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </div>
        </div>

        <div className={classes.columnRight}>
          <div className={classes.blocRight}>
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1, transition: { delay: 0.1 } }}
            >
              <img
                className={classes.logo}
                src="assets/images/logos/logo.png"
                alt="logo"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BoundaryErrorPage;
